export enum RequestType {
	SEASONAL = 'SEASONAL',
	ONE_TIME = 'ONE_TIME',
}

export interface Crop {
	id: string;
	name: string;
	image_url: string;
}

export interface Country {
	name: any;
	alpha_two: string;
	alpha_three?: string;
}

export interface Farmer {
	id: string;
	user_id: string;
	first_name: string;
	last_name: string;
	full_name: string;
	profile_image_url: string;
	country?: Country;
}

export interface Location {
	address: string;
	latitude: number;
	longitude: number;
}

export interface FarmDetails {
	area: number;
	unit: string;
	location: Location;
}

export interface Language {
	id: string;
	name: string;
}

export interface Attachment {
	id: string;
	name: string;
	url: string;
}

export interface Attachments {
	files: Attachment[];
	images: Attachment[];
}

export interface Duration {
	months: number;
	days: number;
}

// For detailed offer view
export interface SeasonalOneTimeOffer {
	id: string;
	crop: Crop;
	farmer: Farmer;
	sent_date: string;
	farmer_id: string;
	request_type: RequestType;
	duration_days?: string;
	duration_timestamp?: string;
	request_valid_for_days?: string;
	request_valid_for_timestamp?: string;
	farm_details: FarmDetails;
	communication_languages: Language[];
	description: string;
	attachments: string[];
	start_date?: string;
	end_date?: string;
	duration?: Duration;
	is_sent?: boolean;
	is_paid?: boolean;
	payment_status?: string;
	request_active_days?: string;
	request_active_timestamp?: string;
	price?: number;
	currency?: string;
	f100_url?: string;
	offer_id?: string;
	channelId?: string;
	request_id?: string;
	original_date?: {
		start_date: string;
		end_date: string;
	};
}

// For listing view
export interface OfferListItem {
	id: string;
	f100_url?: string;
	channelId?: string;
	sent_date: string;
	crops: Crop[];
	request_type: RequestType;
	start_date?: string;
	request_id?: string;
	end_date?: string;
	duration?: Duration;
	is_sent: boolean;
	is_paid: boolean;
	payment_status?: string;
	request_active_days?: string;
	request_active_timestamp?: string;
	farmer: Farmer;
	price?: number;
	currency?: string;
	original_date?: {
		start_date: string;
		end_date: string;
	};
}

export interface PaginationData {
	total_pages: number;
	current_page: number;
	items_per_page: number;
	total_items: number;
}

export interface OffersListResponse {
	data: OfferListItem[];
	pagination: PaginationData;
}

export type OfferStatus = 'active' | 'archived' | 'completed';

// API Response Interfaces
export interface APIResponse {
	success: boolean;
	data: {
		totalData: number;
		totalPages: number;
		currentPage: number;
		hasNextPage: boolean;
		data: OfferListItem[];
	};
	error?: string;
}

export interface RequestDetailResponse {
	success: boolean;
	data: {
		id: string;
		farmer_id: string;
		specialist_id: string;
		services_id: string;
		request_type: string;
		start_date: string;
		end_date: string;
		crop_id: string;
		area: number;
		unit: string;
		location_name: string;
		latitude: number;
		longitude: number;
		communication_language: string[];
		problem_description: string;
		specialist_connection_code: string | null;
		offer_id: string | null;
		payment_status: string;
		request_status: string;
		request_files: any[];
		request_id: string;
		created_at: string;
		updated_at: string;
		services?: {
			price: string;
			currency: string;
		};
		original_date?: {
			start_date: string;
			end_date: string;
		};
	};
	error: string;
}

export interface UserDataResponse {
	success: boolean;
	data: {
		id: string;
		user_id: string;
		first_name: string;
		last_name: string;
		profile_image: string;
		country: {
			id: string;
			name: string;
			alpha_two: string;
			alpha_three?: string;
		};
		unicode?: {
			alpha_two: string;
			alpha_three?: string;
		};
	};
	error: string;
}

export interface ServiceDataResponse {
	success: boolean;
	data: {
		id: string;
		name: string;
		price: number;
		currency: string;
		duration: string;
	}[];
	error: string;
}

export interface CropDataResponse {
	success: boolean;
	data: {
		id: string;
		name: string;
		image_url: string;
		country_code: string;
	}[];
	error: string;
}

export interface LanguageDataResponse {
	success: boolean;
	data: {
		id: string;
		name: string;
	}[];
	error: string;
}

export interface CombinedRequestDetail extends SeasonalOneTimeOffer {
	request: RequestDetailResponse['data'];
	user: UserDataResponse['data'];
	services: ServiceDataResponse['data'];
}

export interface SendOfferRequest {
	price: number;
	currency: string;
	message?: string;
	isFree?: boolean;
}

export interface CreateOfferRequest {
	farmer_id: string;
	specialist_id: string;
	request_id: string;
	price: string;
	currency: string;
	is_free: boolean;
	is_default_price: boolean;
	consultation_type: string;
	offer_status: string;
	offer_comment: string;
}

export interface CreateOfferResponse {
	success: boolean;
	data: {
		id: string;
		[key: string]: any;
	};
	message: string;
}

export interface OfferDetails {
	id: string;
	farmer_id: string;
	specialist_id: string;
	request_id: string;
	price: string;
	currency: string;
	is_default_price: boolean;
	is_free: boolean;
	consultation_type: string;
	offer_status: string;
	offer_comment: string;
	created_at: string;
	updated_at: string;
}

export interface OfferDetailsResponse {
	success: boolean;
	data: OfferDetails;
	error: string;
}
